import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";
import { FormConfig, IsFormable } from "@/lib/formable";
import { formatCurrency } from "@/lib/utility/currency-helper";
import {
  MrfiktivVehicleInsuranceContractViewModelGen,
  MrfiktivVehicleLeasingContractViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import {
  MrfiktivVehicleInsuranceContractGen,
  MrfiktivVehicleLeasingContractGen
} from "../services/mrfiktiv/v1/data-contracts";
import { VehicleContractBase } from "./vehicle-contract-base.entity";
import { VehicleContractType } from "./types/vehicle-contract-type";
import { VehicleContractTypeEnum } from "@/lib/enum/vehicle-contract-type.enum";

export class VehicleInsuranceContract
  extends VehicleContractBase
  implements MrfiktivVehicleInsuranceContractViewModelGen
{
  electronicInsuranceConfirmationCode?: string | null;
  hasGapCoverage?: boolean | null;
  fullComprehensiveDeductible?: number | null;
  partialCoverageDeductible?: number | null;
  hasRoadsideAssistance?: boolean | null;
  maxTowingAmount?: number | null;
  maxRoadsideCoverageAmount?: number | null;
  rentalCarDailyRate?: number | null;
  rentalCarMaxDays?: number | null;
  maxBreakdownAssistanceAmount?: number | null;
  liabilityCoverageCostYearly?: number | null;
  partialCoverageCostYearly?: number | null;
  fullComprehensiveCostYearly?: number | null;

  isFullComprehensiveCovered?: boolean | null;
  isWrappingCovered?: boolean | null;
  fullComprehensiveAppraiserLimit?: number | null;
  fullComprehensiveNotes?: string | null;
  fullComprehensiveExclusions?: string | null;

  isPartialCoverageIncluded?: boolean | null;
  partialCoverageAppraiserLimit?: number | null;
  partialCoverageNotes?: string | null;
  partialCoverageExclusions?: string | null;

  roadsideAssistanceNotes?: string | null;
  roadsideAssistanceExclusions?: string | null;

  gapCoverageNotes?: string | null;
  gapCoverageExclusions?: string | null;

  get rentalCarDailyRateReadable(): string {
    if (!this.rentalCarDailyRate) {
      return "";
    }
    return formatCurrency(this.rentalCarDailyRate, this.currency || CurrencyEnum.EUR);
  }

  constructor(contract: Partial<MrfiktivVehicleInsuranceContractViewModelGen>) {
    super(contract);

    this.electronicInsuranceConfirmationCode = contract.electronicInsuranceConfirmationCode;
    this.hasGapCoverage = contract.hasGapCoverage;
    this.fullComprehensiveDeductible = contract.fullComprehensiveDeductible;
    this.partialCoverageDeductible = contract.partialCoverageDeductible;
    this.hasRoadsideAssistance = contract.hasRoadsideAssistance;
    this.maxTowingAmount = contract.maxTowingAmount;
    this.maxRoadsideCoverageAmount = contract.maxRoadsideCoverageAmount;
    this.rentalCarDailyRate = contract.rentalCarDailyRate;
    this.rentalCarMaxDays = contract.rentalCarMaxDays;
    this.maxBreakdownAssistanceAmount = contract.maxBreakdownAssistanceAmount;
    this.liabilityCoverageCostYearly = contract.liabilityCoverageCostYearly;
    this.partialCoverageCostYearly = contract.partialCoverageCostYearly;
    this.fullComprehensiveCostYearly = contract.fullComprehensiveCostYearly;

    this.isFullComprehensiveCovered = contract.isFullComprehensiveCovered;
    this.isWrappingCovered = contract.isWrappingCovered;
    this.fullComprehensiveAppraiserLimit = contract.fullComprehensiveAppraiserLimit;
    this.fullComprehensiveNotes = contract.fullComprehensiveNotes;
    this.fullComprehensiveExclusions = contract.fullComprehensiveExclusions;

    this.isPartialCoverageIncluded = contract.isPartialCoverageIncluded;
    this.partialCoverageAppraiserLimit = contract.partialCoverageAppraiserLimit;
    this.partialCoverageNotes = contract.partialCoverageNotes;
    this.partialCoverageExclusions = contract.partialCoverageExclusions;

    this.roadsideAssistanceNotes = contract.roadsideAssistanceNotes;
    this.roadsideAssistanceExclusions = contract.roadsideAssistanceExclusions;

    this.gapCoverageNotes = contract.gapCoverageNotes;
    this.gapCoverageExclusions = contract.gapCoverageExclusions;

    this.contractType = VehicleContractTypeEnum.INSURANCE;
  }

  get dto(): MrfiktivVehicleInsuranceContractGen {
    return {
      contractType: this.contractType,
      number: this.number,
      electronicInsuranceConfirmationCode: this.electronicInsuranceConfirmationCode,
      companyId: this.companyId || null,
      title: this.title,
      description: this.description,
      currency: this.currency,
      startDate: this.startDate?.length === 0 ? null : this.startDate,
      endDate: this.endDate?.length === 0 ? null : this.endDate,
      hasGapCoverage: this.hasGapCoverage,
      fullComprehensiveDeductible: this.fullComprehensiveDeductible ? Number(this.fullComprehensiveDeductible) : null,
      partialCoverageDeductible: this.partialCoverageDeductible ? Number(this.partialCoverageDeductible) : null,
      hasRoadsideAssistance: this.hasRoadsideAssistance,
      maxTowingAmount: this.maxTowingAmount ? Number(this.maxTowingAmount) : null,
      maxRoadsideCoverageAmount: this.maxRoadsideCoverageAmount ? Number(this.maxRoadsideCoverageAmount) : null,
      rentalCarDailyRate: this.rentalCarDailyRate ? Number(this.rentalCarDailyRate) : null,
      rentalCarMaxDays: this.rentalCarMaxDays ? Number(this.rentalCarMaxDays) : null,
      maxBreakdownAssistanceAmount: this.maxBreakdownAssistanceAmount
        ? Number(this.maxBreakdownAssistanceAmount)
        : null,
      liabilityCoverageCostYearly: this.liabilityCoverageCostYearly ? Number(this.liabilityCoverageCostYearly) : null,
      partialCoverageCostYearly: this.partialCoverageCostYearly ? Number(this.partialCoverageCostYearly) : null,
      fullComprehensiveCostYearly: this.fullComprehensiveCostYearly ? Number(this.fullComprehensiveCostYearly) : null,
      refs: this.refs || [],
      isFullComprehensiveCovered: this.isFullComprehensiveCovered ?? undefined,
      isWrappingCovered: this.isWrappingCovered ?? undefined,
      fullComprehensiveAppraiserLimit: this.fullComprehensiveAppraiserLimit
        ? Number(this.fullComprehensiveAppraiserLimit)
        : undefined,
      fullComprehensiveNotes: this.fullComprehensiveNotes ?? undefined,
      fullComprehensiveExclusions: this.fullComprehensiveExclusions ?? undefined,
      isPartialCoverageIncluded: this.isPartialCoverageIncluded ?? undefined,
      partialCoverageAppraiserLimit: this.partialCoverageAppraiserLimit
        ? Number(this.partialCoverageAppraiserLimit)
        : undefined,
      partialCoverageNotes: this.partialCoverageNotes ?? undefined,
      partialCoverageExclusions: this.partialCoverageExclusions ?? undefined,
      roadsideAssistanceNotes: this.roadsideAssistanceNotes ?? undefined,
      roadsideAssistanceExclusions: this.roadsideAssistanceExclusions ?? undefined,
      gapCoverageNotes: this.gapCoverageNotes ?? undefined,
      gapCoverageExclusions: this.gapCoverageExclusions ?? undefined
    };
  }
}

@IsFormable
export class VehicleLeasingContract extends VehicleContractBase implements MrfiktivVehicleLeasingContractViewModelGen {
  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.startMilage"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.startMilage"
    }
  })
  startMilage?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.maxMilage"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.maxMilage"
    }
  })
  maxMilage?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.maxReimbursedMilage"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.maxReimbursedMilage"
    }
  })
  maxReimbursedMilage?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.creditForReimbursedMilage"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.creditForReimbursedMilage"
    }
  })
  creditForReimbursedMilage?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.freeMileageAllowance"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.freeMileageAllowance"
    }
  })
  freeMileageAllowance?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.excessMileageCosts"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.excessMileageCosts"
    }
  })
  excessMileageCosts?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.purchasePrice"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.purchasePrice"
    }
  })
  purchasePrice?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.buybackPrice"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.buybackPrice"
    }
  })
  buybackPrice?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.leasing.leaseCostMonthly"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.leasing.leaseCostMonthly"
    }
  })
  leaseCostMonthly?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.leasing.leaseCostSpecial"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.leasing.leaseCostSpecial"
    }
  })
  leaseCostSpecial?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.leasing.approvalLimit"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "number",
      label: "objects.contract.leasing.approvalLimit"
    }
  })
  approvalLimit?: number | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.leasing.isTechnicalServiceIncluded"],
    type: DetailFormComponentsEnum.CHECK_BOX,
    props: {
      label: "objects.contract.leasing.isTechnicalServiceIncluded"
    }
  })
  isTechnicalServiceIncluded?: boolean | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.leasing.depreciationCompensation"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "text",
      label: "objects.contract.leasing.depreciationCompensation"
    }
  })
  depreciationCompensation?: string | null;

  constructor(contract: Partial<MrfiktivVehicleLeasingContractViewModelGen>) {
    super(contract);

    this.number = contract?.number;
    this.title = contract?.title ?? "";
    this.description = contract?.description;
    this.buybackPrice = contract?.buybackPrice;
    this.creditForReimbursedMilage = contract?.creditForReimbursedMilage;
    this.currency = contract?.currency as CurrencyEnum;
    this.endDate = contract?.endDate;
    this.excessMileageCosts = contract?.excessMileageCosts;
    this.freeMileageAllowance = contract?.freeMileageAllowance;
    this.maxMilage = contract?.maxMilage;
    this.maxReimbursedMilage = contract?.maxReimbursedMilage;
    this.purchasePrice = contract?.purchasePrice;
    this.startMilage = contract?.startMilage;
    this.startDate = contract?.startDate;
    this.leaseCostMonthly = contract?.leaseCostMonthly;
    this.leaseCostSpecial = contract?.leaseCostSpecial;
    this.approvalLimit = contract?.approvalLimit;
    this.isTechnicalServiceIncluded = contract?.isTechnicalServiceIncluded;
    this.depreciationCompensation = contract?.depreciationCompensation;

    this.contractType = VehicleContractTypeEnum.LEASING;
  }

  get dto(): MrfiktivVehicleLeasingContractGen {
    return {
      contractType: this.contractType,
      number: this.number,
      companyId: this.companyId || null,
      title: this.title,
      description: this.description,
      currency: this.currency,
      startDate: this.startDate?.length === 0 ? null : this.startDate,
      endDate: this.endDate?.length === 0 ? null : this.endDate,
      startMilage: this.startMilage ? Number(this.startMilage) : undefined,
      maxMilage: this.maxMilage ? Number(this.maxMilage) : undefined,
      freeMileageAllowance: this.freeMileageAllowance ? Number(this.freeMileageAllowance) : undefined,
      maxReimbursedMilage: this.maxReimbursedMilage ? Number(this.maxReimbursedMilage) : undefined,
      purchasePrice: this.purchasePrice ? Number(this.purchasePrice) : undefined,
      buybackPrice: this.buybackPrice ? Number(this.buybackPrice) : undefined,
      creditForReimbursedMilage: this.creditForReimbursedMilage ? Number(this.creditForReimbursedMilage) : undefined,
      excessMileageCosts: this.excessMileageCosts ? Number(this.excessMileageCosts) : undefined,
      leaseCostMonthly: this.leaseCostMonthly ? Number(this.leaseCostMonthly) : undefined,
      leaseCostSpecial: this.leaseCostSpecial ? Number(this.leaseCostSpecial) : undefined,
      refs: this.refs || [],
      approvalLimit: this.approvalLimit ? Number(this.approvalLimit) : undefined,
      isTechnicalServiceIncluded: this.isTechnicalServiceIncluded ?? undefined,
      depreciationCompensation: this.depreciationCompensation ?? undefined
    };
  }
}

export type IVehicleContractDto = VehicleContractType & {
  partnerId: string;
  vehicleId: string;
  vehicleDisplayName: string;
};
